import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export function CityAutocomplete({ value, onChange, country = "FR", placeholder = "Paris", className = "", testid = "city-autocomplete", required = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const pickedRef = useRef(false);

  useEffect(() => {
    if (pickedRef.current) { pickedRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 2) { setItems([]); setOpen(false); return; }
    const tmr = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api(`/geo/city-suggest?q=${encodeURIComponent(q)}&country=${country}`, { auth: false });
        setItems(r.suggestions || []);
        setOpen((r.suggestions || []).length > 0);
      } catch { setItems([]); } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(tmr);
  }, [value, country]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(name) {
    pickedRef.current = true;
    onChange(name);
    setOpen(false);
  }

  return (
    <div ref={boxRef} className="relative">
      <Input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => items.length && setOpen(true)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        data-testid={testid} />
      {loading && <FindoSpinner size={15} className="absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && (
        <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-border bg-white shadow-lg" data-testid={`${testid}-list`}>
          {items.map((it, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => pick(it.name)}
                className="w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                data-testid={`${testid}-option`}>
                <MapPin size={14} className="mt-0.5 shrink-0 text-findo-red" />
                <span><span className="font-semibold text-slate-800">{it.name}</span>
                  <span className="block text-xs text-slate-400 truncate">{it.label}</span></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
